import ClaireHelpButton from "../components/ClaireHelpButton";
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import {
  Search, Filter, ExternalLink, Loader2, RefreshCw, Sparkles, ArrowRight,
  Zap, BookOpen, ChevronDown, ChevronUp, Globe, Mail, Phone, Building2,
  ListChecks, Send, Database, Wand2, X, ChevronLeft, ChevronRight, Check,
  CheckCircle2, FileSpreadsheet, Trello, Mail as MailIcon, Loader,
} from "lucide-react";
import Sidebar from "../components/Sidebar";
import ErrorState from "../components/ErrorState";
import LangToggle from "../components/LangToggle";
import ThemeToggle from "../components/ThemeToggle";
import Logo from "../components/Logo";
import { useLang } from "../i18n/LangContext";
import { SettingsTabs } from "../components/SettingsTabs";
import { useTrialLock } from "../hooks/useTrialLock";
import { blockExportIfTrial } from "../components/TrialLock";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ----- Source taxonomy enrichment ---------------------------------------------------
// Maps a backend "type" string into UX-rich metadata: difficulty, tags, etc.
function enrichSource(src) {
  const type = (src.type || "").toLowerCase();
  let difficulty = "beginner";    // beginner | intermediate | advanced
  let cost = "free";              // free | freemium | credits
  let dataTags = [];              // e.g., ["email","phone","address","social"]
  if (type.includes("scraper") || type.includes("extens")) {
    difficulty = "intermediate";
    cost = "credits";
    dataTags = ["company", "phone", "address", "website"];
  }
  if (type.includes("email")) {
    difficulty = "beginner";
    cost = "freemium";
    dataTags = ["email"];
  }
  if (type.includes("banco") || type.includes("b2b")) {
    difficulty = "intermediate";
    cost = "credits";
    dataTags = ["email", "phone", "title", "company"];
  }
  if (type.includes("linkedin")) {
    difficulty = "advanced";
    cost = "credits";
    dataTags = ["email", "phone", "title", "company"];
  }
  if (type.includes("diretório") || type.includes("busca pública") || type.includes("local")) {
    difficulty = "beginner";
    cost = "free";
    dataTags = ["company", "phone", "address", "website"];
  }
  if (type.includes("licitações") || type.includes("portal estadual")
      || type.includes("histórico") || type.includes("certificações")) {
    difficulty = src.golden ? "advanced" : "beginner";
    cost = src.golden ? "credits" : "free";
    dataTags = ["company", "agency", "deadline", "value"];
  }
  if ((src.credits || "").toLowerCase().includes("ilimitado")) cost = "free";
  if ((src.credits || "").toLowerCase().includes("grátis")) cost = "free";
  return { ...src, _difficulty: difficulty, _cost: cost, _data: dataTags };
}

const TYPE_GROUPS = [
  { id: "all",       labelKey: "fs.group_all" },
  { id: "directory", labelKey: "fs.group_directory", match: ["diretório", "busca pública", "local"] },
  { id: "email",     labelKey: "fs.group_email",     match: ["email"] },
  { id: "scraper",   labelKey: "fs.group_scraper",   match: ["scraper", "extensão", "extens", "no-code"] },
  { id: "b2b",       labelKey: "fs.group_b2b",       match: ["banco", "b2b", "linkedin"] },
];

const DIFF_COLORS = {
  beginner:     { bg: "#DCFCE7", text: "#16A34A", labelKey: "fs.diff_beginner" },
  intermediate: { bg: "#FEF3C7", text: "#B45309", labelKey: "fs.diff_intermediate" },
  advanced:     { bg: "#FEE2E2", text: "#DC2626", labelKey: "fs.diff_advanced" },
};

const COST_COLORS = {
  free:     { bg: "#DCFCE7", text: "#16A34A", labelKey: "fs.cost_free" },
  freemium: { bg: "#EFF6FF", text: "#3B82F6", labelKey: "fs.cost_freemium" },
  credits:  { bg: "#F3E8FF", text: "#8B5CF6", labelKey: "fs.cost_credits" },
};

// ----- Playbooks (curated, source-agnostic with recommended sources) -----------------
const PLAYBOOKS = [
  {
    id: "local-b2b",
    icon: Building2, color: "#3B82F6",
    titleKey: "fs.pb1_title",  bodyKey: "fs.pb1_body",
    sources: ["Google Maps", "BBB Florida", "Yellow Pages Florida"],
    objective: "local_b2b",
    location: "Orlando, FL",
    business_type: "Roofing",
  },
  {
    id: "validate-emails",
    icon: Mail, color: "#A855F7",
    titleKey: "fs.pb2_title",  bodyKey: "fs.pb2_body",
    sources: ["Hunter.io", "Prospeo.io", "Snov.io"],
    objective: "validate_emails",
  },
  {
    id: "construction-decisores",
    icon: Wand2, color: "#F59E0B",
    titleKey: "fs.pb3_title",  bodyKey: "fs.pb3_body",
    sources: ["Apollo.io", "Lusha", "BBB Construction Florida"],
    objective: "decision_makers",
    business_type: "General Contracting",
  },
  {
    id: "high-rating-bulk",
    icon: Sparkles, color: "#22C55E",
    titleKey: "fs.pb4_title",  bodyKey: "fs.pb4_body",
    sources: ["Google Maps", "Outscraper", "Yelp Florida"],
    objective: "high_rating_bulk",
    business_type: "HVAC",
    location: "Tampa, FL",
  },
];

// ----- FAQ ---------------------------------------------------------------------------
const FAQ = [
  { qKey: "fs.faq1_q", aKey: "fs.faq1_a" },
  { qKey: "fs.faq2_q", aKey: "fs.faq2_a" },
  { qKey: "fs.faq3_q", aKey: "fs.faq3_a" },
  { qKey: "fs.faq4_q", aKey: "fs.faq4_a" },
];

export default function FreeSources() {
  const { t, lang } = useLang();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [groupFilter, setGroupFilter] = useState("all");
  const [nicheTab, setNicheTab] = useState("all"); // all | general | gov
  const [costFilter, setCostFilter] = useState(""); // ""|free|freemium|credits
  const [diffFilter, setDiffFilter] = useState("");
  const [openFaq, setOpenFaq] = useState(null);
  // Wizard state
  const [wizardOpen, setWizardOpen] = useState(false);
  const [wizardSeed, setWizardSeed] = useState(null); // {sourceName?, playbook?}

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const { data: fs } = await axios.get(`${API}/free-sources`);
      setData(fs);
    } catch (e) {
      setError(e?.message || "Failed to load");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const enriched = useMemo(
    () => (data?.sources || []).map(enrichSource),
    [data]
  );

  const recommended = useMemo(() => {
    // "Smart picks" = first one of each group; favor free + beginner
    const byCost = enriched.filter((s) => s._cost === "free" && s._difficulty === "beginner");
    return byCost.slice(0, 3);
  }, [enriched]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const group = TYPE_GROUPS.find((g) => g.id === groupFilter);
    const list = enriched.filter((s) => {
      if (nicheTab === "gov" && s.niche !== "gov") return false;
      if (nicheTab === "general" && s.niche === "gov") return false;
      if (q && !`${s.name} ${s.type} ${s.extracts}`.toLowerCase().includes(q)) return false;
      if (group?.match) {
        const tt = (s.type || "").toLowerCase();
        if (!group.match.some((m) => tt.includes(m))) return false;
      }
      if (costFilter && s._cost !== costFilter) return false;
      if (diffFilter && s._difficulty !== diffFilter) return false;
      return true;
    });
    // Golden cards (SAM.gov / USAspending) always float to the top
    return list.sort((a, b) => (b.golden ? 1 : 0) - (a.golden ? 1 : 0));
  }, [enriched, search, groupFilter, nicheTab, costFilter, diffFilter]);

  const launchWizard = (seed = null) => {
    setWizardSeed(seed);
    setWizardOpen(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--al-bg)] text-[var(--al-text)]">
        <Sidebar />
        <div className="ml-[var(--al-sidebar-w,260px)] flex items-center justify-center h-screen">
          <Loader2 className="w-8 h-8 animate-spin text-[#3B82F6]" />
        </div>
      </div>
    );
  }
  if (error || !data) {
    return (
      <div className="min-h-screen bg-[var(--al-bg)] text-[var(--al-text)]">
        <Sidebar />
        <div className="ml-[var(--al-sidebar-w,260px)] px-8 py-16">
          <ErrorState testid="free-sources-error" title={t("fs.error_title")} message={error} onRetry={load} />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--al-bg)] text-[var(--al-text)]">
      <Sidebar />
      <div className="ml-[var(--al-sidebar-w,260px)]">
        <header
          data-testid="free-sources-header"
          className="sticky top-0 z-30 bg-[var(--al-card-bg)] border-b border-[var(--al-border)] px-8 py-4 flex items-center justify-between"
        >
          <div className="flex items-center gap-4">
            <Logo className="w-9 h-9 object-contain" />
            <div>
              <h1 className="text-[16px] font-semibold text-[var(--al-text)]">{t("fs.page_title_v2")}</h1>
              <p className="text-[12.5px] text-[var(--al-text-muted)] mt-0.5">{t("fs.page_subtitle_v2")}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <ThemeToggle />
            <LangToggle />
            <button
              type="button"
              data-testid="fs-launch-wizard"
              onClick={() => launchWizard()}
              className="al-btn-primary inline-flex items-center gap-1.5 px-3 h-9 rounded-lg text-[12.5px] font-semibold"
            >
              <Zap className="w-3.5 h-3.5" /> {t("fs.cta_engine")}
            </button>
            <button
              data-testid="free-sources-refresh-btn"
              onClick={load}
              className="flex items-center gap-1.5 px-3 h-9 rounded-lg border border-[var(--al-border)] bg-[var(--al-card-bg)] text-[12.5px] font-medium hover:border-[#3B82F6] hover:text-[#3B82F6]"
            >
              <RefreshCw className="w-3.5 h-3.5" />
            </button>
            <ClaireHelpButton testid="free-sources"
              question="Como uso as Free Sources — fontes gratuitas de leads e o motor de captura?" />
          </div>
        </header>

        <SettingsTabs isPt={lang === "pt"} />

        <main className="px-6 lg:px-10 py-8 max-w-[1400px] mx-auto space-y-8">
          {/* Hero */}
          <section data-testid="fs-hero" className="al-card p-7 bg-gradient-to-br from-[#EFF6FF] to-white dark:from-[#0F172A] dark:to-[#18181B] border-[#DBEAFE] dark:border-[#1E293B]">
            <div className="flex items-start gap-5">
              <div className="w-14 h-14 rounded-2xl bg-[#3B82F6] text-white flex items-center justify-center shrink-0">
                <Database className="w-7 h-7" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="inline-flex items-center gap-1.5 text-[11px] font-bold text-[#3B82F6] bg-white px-2 py-0.5 rounded-full mb-2 shadow-sm">
                  <Sparkles className="w-3 h-3" /> {t("fs.hero_eyebrow")}
                </div>
                <h2 className="text-[26px] sm:text-[32px] font-extrabold leading-tight tracking-tight text-[var(--al-text)]">
                  {t("fs.hero_title")}
                </h2>
                <p className="text-[13.5px] text-[var(--al-text-muted)] mt-2 leading-relaxed max-w-[680px]">
                  {t("fs.hero_body")}
                </p>
                <div className="mt-4 flex items-center gap-2 flex-wrap">
                  <button
                    type="button"
                    data-testid="fs-hero-cta"
                    onClick={() => launchWizard()}
                    className="al-btn-primary inline-flex items-center gap-1.5 h-10 px-4 rounded-lg text-[13px] font-semibold"
                  >
                    <Zap className="w-4 h-4" /> {t("fs.cta_engine")}
                  </button>
                  <span className="text-[11.5px] text-[var(--al-text-muted)]">
                    {data.count} {t("fs.curated_count")}
                  </span>
                </div>
              </div>
            </div>
          </section>

          {/* Recommended */}
          <section data-testid="fs-recommended">
            <h3 className="text-[14px] font-bold text-[var(--al-text)] mb-3 flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-[#A855F7]" /> {t("fs.recommended_title")}
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {recommended.map((s) => (
                <SourceCard key={s.id} src={s} variant="recommended" onLaunch={() => launchWizard({ sourceName: s.name })} />
              ))}
            </div>
          </section>

          {/* Niche tabs — General Prospecting vs Gov & Construction */}
          <section data-testid="fs-niche-tabs" className="flex gap-2 flex-wrap">
            {[
              { id: "all",     label: t("fs.tab_all") },
              { id: "general", label: t("fs.tab_general") },
              { id: "gov",     label: t("fs.tab_gov") },
            ].map((tb) => (
              <button
                key={tb.id}
                type="button"
                data-testid={`fs-tab-${tb.id}`}
                onClick={() => setNicheTab(tb.id)}
                className={`h-10 px-4 rounded-full text-[12.5px] font-bold border-2 transition-all ${
                  nicheTab === tb.id
                    ? tb.id === "gov"
                      ? "border-[#C9A24E] bg-[#C9A24E]/10 text-[#C9A24E]"
                      : "border-[#3B82F6] bg-[#EFF6FF]/60 text-[#3B82F6]"
                    : "border-[var(--al-border)] text-[var(--al-text-muted)] hover:border-[#3B82F6]/50"}`}
              >
                {tb.label}
              </button>
            ))}
          </section>

          {/* Filters bar */}
          <section data-testid="fs-filters" className="al-card p-3">
            <div className="flex items-center gap-2 flex-wrap">
              <div className="relative flex-1 min-w-[260px]">
                <Search className="w-3.5 h-3.5 text-[var(--al-text-muted)] absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  data-testid="fs-search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={t("fs.search_placeholder")}
                  className="al-input h-9 pl-8 text-[12.5px] w-full"
                />
              </div>
              <select
                data-testid="fs-filter-group"
                value={groupFilter}
                onChange={(e) => setGroupFilter(e.target.value)}
                className="al-input h-9 text-[12.5px] min-w-[160px]"
              >
                {TYPE_GROUPS.map((g) => (<option key={g.id} value={g.id}>{t(g.labelKey)}</option>))}
              </select>
              <select
                data-testid="fs-filter-cost"
                value={costFilter}
                onChange={(e) => setCostFilter(e.target.value)}
                className="al-input h-9 text-[12.5px] min-w-[140px]"
              >
                <option value="">{t("fs.cost_any")}</option>
                <option value="free">{t("fs.cost_free")}</option>
                <option value="freemium">{t("fs.cost_freemium")}</option>
                <option value="credits">{t("fs.cost_credits")}</option>
              </select>
              <select
                data-testid="fs-filter-diff"
                value={diffFilter}
                onChange={(e) => setDiffFilter(e.target.value)}
                className="al-input h-9 text-[12.5px] min-w-[160px]"
              >
                <option value="">{t("fs.diff_any")}</option>
                <option value="beginner">{t("fs.diff_beginner")}</option>
                <option value="intermediate">{t("fs.diff_intermediate")}</option>
                <option value="advanced">{t("fs.diff_advanced")}</option>
              </select>
              {(search || groupFilter !== "all" || costFilter || diffFilter) && (
                <button
                  type="button"
                  data-testid="fs-filter-clear"
                  onClick={() => { setSearch(""); setGroupFilter("all"); setCostFilter(""); setDiffFilter(""); }}
                  className="text-[11.5px] text-[var(--al-text-muted)] hover:text-[#DC2626] underline"
                >
                  {t("fs.filter_clear")}
                </button>
              )}
              <span className="ml-auto text-[11.5px] text-[var(--al-text-muted)]">
                <strong className="text-[var(--al-text)]">{filtered.length}</strong>/{enriched.length} {t("fs.sources_count")}
              </span>
            </div>
          </section>

          {/* Cards grid */}
          <section data-testid="fs-cards-grid">
            {filtered.length === 0 ? (
              <div className="al-card p-10 text-center text-[13px] text-[var(--al-text-muted)]">
                {t("fs.empty_filtered")}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {filtered.map((s) => (
                  <SourceCard key={s.id} src={s} onLaunch={() => launchWizard({ sourceName: s.name })} />
                ))}
              </div>
            )}
          </section>

          {/* Playbooks */}
          <section data-testid="fs-playbooks">
            <h3 className="text-[14px] font-bold text-[var(--al-text)] mb-3 flex items-center gap-2">
              <BookOpen className="w-4 h-4 text-[#3B82F6]" /> {t("fs.playbooks_title")}
            </h3>
            <p className="text-[12.5px] text-[var(--al-text-muted)] mb-4 max-w-[720px]">{t("fs.playbooks_subtitle")}</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {PLAYBOOKS.map((pb) => (
                <PlaybookCard key={pb.id} pb={pb} onLaunch={() => launchWizard({ playbook: pb })} />
              ))}
            </div>
          </section>

          {/* FAQ */}
          <section data-testid="fs-faq" className="al-card p-6">
            <h3 className="text-[14px] font-bold text-[var(--al-text)] mb-3">{t("fs.faq_title")}</h3>
            <div className="divide-y divide-[var(--al-border)]">
              {FAQ.map((f, i) => (
                <button
                  key={f.qKey}
                  type="button"
                  data-testid={`fs-faq-${i}`}
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full text-left py-3 flex items-start justify-between gap-3 hover:text-[#3B82F6]"
                >
                  <span className="text-[13px] font-semibold text-[var(--al-text)]">{t(f.qKey)}</span>
                  <span className="shrink-0 mt-0.5">{openFaq === i ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}</span>
                </button>
              ))}
              {openFaq !== null && (
                <p data-testid={`fs-faq-answer-${openFaq}`} className="pb-4 text-[12.5px] text-[var(--al-text-muted)] leading-relaxed">
                  {t(FAQ[openFaq].aKey)}
                </p>
              )}
            </div>
          </section>

          {/* Final CTA */}
          <section className="al-card p-7 text-center bg-[#1A1A2E] text-white border-[#1A1A2E]">
            <h3 className="text-[20px] font-bold">{t("fs.final_cta_title")}</h3>
            <p className="text-[13px] text-white/75 mt-1.5 max-w-[520px] mx-auto">{t("fs.final_cta_body")}</p>
            <button
              type="button"
              data-testid="fs-final-cta"
              onClick={() => launchWizard()}
              className="mt-4 inline-flex items-center gap-1.5 h-10 px-5 rounded-lg bg-[#3B82F6] hover:bg-[#2563EB] text-white text-[13px] font-bold"
            >
              <Zap className="w-4 h-4" /> {t("fs.cta_engine")}
            </button>
          </section>
        </main>
      </div>

      <ProspectingWizard
        open={wizardOpen}
        seed={wizardSeed}
        sources={enriched}
        onClose={() => setWizardOpen(false)}
      />
    </div>
  );
}

// ============== Source Card =====================================================
function SourceCard({ src, variant, onLaunch }) {
  const { t } = useLang();
  const navigate = useNavigate();
  const diff = DIFF_COLORS[src._difficulty] || DIFF_COLORS.beginner;
  const cost = COST_COLORS[src._cost] || COST_COLORS.free;
  const isRecommended = variant === "recommended";
  const isGolden = !!src.golden;

  return (
    <div
      data-testid={`fs-card-${src.id}`}
      className={`al-card p-4 flex flex-col h-full ${
        isGolden ? "ring-2 ring-[#C9A24E]/70 relative overflow-hidden"
        : isRecommended ? "ring-1 ring-[#A855F7]/40" : ""}`}
    >
      {isGolden && (
        <div className="absolute top-0 right-0 text-[9px] font-extrabold uppercase tracking-widest px-2.5 py-1 rounded-bl-lg bg-[#C9A24E] text-[#0A1233]"
          data-testid={`fs-golden-badge-${src.id}`}>
          🏛️ {src.badge || "EXPERT · ULTIMATE"}
        </div>
      )}
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 mb-1">
            <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide ${
              isGolden ? "bg-[#C9A24E]/15 text-[#C9A24E]" : "bg-[#EFF6FF] text-[#3B82F6]"}`}>
              #{src.id}
            </span>
            <span className="text-[10.5px] font-semibold uppercase tracking-wider text-[var(--al-text-muted)] truncate">
              {src.type}
            </span>
          </div>
          <h4 className="text-[14px] font-bold text-[var(--al-text)] leading-snug truncate">{src.name}</h4>
        </div>
        {isRecommended && !isGolden && (
          <span className="inline-flex items-center gap-0.5 text-[9.5px] font-bold uppercase px-1.5 py-0.5 rounded bg-[#F3E8FF] text-[#A855F7] shrink-0">
            <Sparkles className="w-2.5 h-2.5" /> {t("fs.tag_recommended")}
          </span>
        )}
      </div>

      <p className="text-[12px] text-[var(--al-text-muted)] line-clamp-2 mb-3 leading-relaxed">{src.extracts}</p>

      <div className="flex items-center gap-1.5 flex-wrap mb-3">
        <span className="text-[10px] font-bold px-1.5 py-0.5 rounded" style={{ background: diff.bg, color: diff.text }}>
          {t(diff.labelKey)}
        </span>
        <span className="text-[10px] font-bold px-1.5 py-0.5 rounded" style={{ background: cost.bg, color: cost.text }}>
          {t(cost.labelKey)}
        </span>
        {src._data.slice(0, 3).map((d) => (
          <span key={d} className="text-[9.5px] font-semibold px-1.5 py-0.5 rounded bg-[var(--al-bg)] text-[var(--al-text-muted)] border border-[var(--al-border)]">
            {d}
          </span>
        ))}
      </div>

      <div className="text-[11px] text-[var(--al-text-muted)] mb-1.5">
        <strong className="text-[var(--al-text)]">{t("fs.card_credits")}: </strong>{src.credits}
      </div>
      {/* Credits transparency: whose credits are burned where */}
      <div className={`text-[10px] font-semibold mb-3 ${
        isGolden || src._cost === "free" ? "text-emerald-600" : "text-amber-600"}`}
        data-testid={`fs-credits-note-${src.id}`}>
        {isGolden
          ? `⚡ ${t("fs.credits_internal")}`
          : src._cost === "free"
            ? `✓ ${t("fs.cost_free")}`
            : `⚠️ ${t("fs.credits_external")}`}
      </div>

      <div className="mt-auto flex items-center gap-2">
        {isGolden && src.internal_route ? (
          <button
            type="button"
            data-testid={`fs-card-engine-${src.id}`}
            onClick={() => navigate(src.internal_route)}
            className="flex-1 inline-flex items-center justify-center gap-1.5 h-8 px-3 rounded-lg text-[11.5px] font-bold bg-[#C9A24E] hover:bg-[#D9B25E] text-[#0A1233]"
          >
            <Zap className="w-3 h-3" /> {t("fs.open_in_app")}
          </button>
        ) : (
          <button
            type="button"
            data-testid={`fs-card-engine-${src.id}`}
            onClick={onLaunch}
            title={t("fs.credits_internal")}
            className="flex-1 al-btn-primary inline-flex items-center justify-center gap-1.5 h-8 px-3 rounded-lg text-[11.5px] font-semibold"
          >
            <Send className="w-3 h-3" /> {t("fs.card_send_engine")}
          </button>
        )}
        <a
          href={src.url}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`fs-card-open-${src.id}`}
          className="inline-flex items-center justify-center w-8 h-8 rounded-lg border border-[var(--al-border)] text-[var(--al-text-muted)] hover:text-[#3B82F6] hover:border-[#3B82F6]"
          title={t("fs.card_open")}
        >
          <ExternalLink className="w-3.5 h-3.5" />
        </a>
      </div>
    </div>
  );
}

// ============== Playbook Card ===================================================
function PlaybookCard({ pb, onLaunch }) {
  const { t } = useLang();
  const Icon = pb.icon;
  return (
    <div data-testid={`fs-pb-${pb.id}`} className="al-card p-5 flex flex-col h-full">
      <div className="flex items-start gap-3 mb-3">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: `${pb.color}1A`, color: pb.color }}>
          <Icon className="w-5 h-5" />
        </div>
        <div className="min-w-0">
          <h4 className="text-[14px] font-bold text-[var(--al-text)]">{t(pb.titleKey)}</h4>
          <p className="text-[12px] text-[var(--al-text-muted)] mt-1 leading-relaxed">{t(pb.bodyKey)}</p>
        </div>
      </div>
      <div className="text-[10.5px] uppercase font-bold tracking-wider text-[var(--al-text-muted)] mb-1">{t("fs.pb_recommended_sources")}</div>
      <ul className="text-[12px] text-[var(--al-text)] mb-4 list-disc pl-5 space-y-0.5">
        {pb.sources.map((s) => <li key={s}>{s}</li>)}
      </ul>
      <button
        type="button"
        data-testid={`fs-pb-cta-${pb.id}`}
        onClick={onLaunch}
        className="mt-auto inline-flex items-center justify-center gap-1.5 h-9 px-3 rounded-lg text-[12px] font-semibold transition-colors"
        style={{ background: `${pb.color}15`, color: pb.color, border: `1px solid ${pb.color}50` }}
      >
        <Zap className="w-3.5 h-3.5" /> {t("fs.pb_run")} <ArrowRight className="w-3 h-3" />
      </button>
    </div>
  );
}

// ============== Prospecting Engine Wizard MVP ==================================
function ProspectingWizard({ open, seed, sources, onClose }) {
  const { t } = useLang();
  const navigate = useNavigate();
  const { onTrial } = useTrialLock();
  const [step, setStep] = useState(0);
  const [objective, setObjective] = useState("local_b2b");
  const [pickedSources, setPickedSources] = useState([]);
  const [businessType, setBusinessType] = useState("");
  const [location, setLocation] = useState("Orlando, FL");
  const [radius, setRadius] = useState("20km");
  const [scoreMax, setScoreMax] = useState(66);
  const [output, setOutput] = useState("crm");
  const [running, setRunning] = useState(false);
  const [results, setResults] = useState(null); // { count, leads, source }

  // Reset on open + seed
  useEffect(() => {
    if (!open) return;
    setStep(0);
    setRunning(false);
    setResults(null);
    if (seed?.playbook) {
      const pb = seed.playbook;
      setObjective(pb.objective || "local_b2b");
      setPickedSources(pb.sources || []);
      setBusinessType(pb.business_type || "");
      setLocation(pb.location || "Orlando, FL");
    } else if (seed?.sourceName) {
      setObjective("custom");
      setPickedSources([seed.sourceName]);
      setBusinessType(""); setLocation("Orlando, FL");
    } else {
      setObjective("local_b2b");
      setPickedSources([]);
      setBusinessType(""); setLocation("Orlando, FL");
    }
  }, [open, seed]);

  if (!open) return null;
  const TOTAL_STEPS = 5;

  const toggleSource = (name) => {
    const activating = !pickedSources.includes(name);
    if (activating) toast.success(`✓ ${name}`, { duration: 1400 });
    setPickedSources((arr) => arr.includes(name) ? arr.filter((s) => s !== name) : [...arr, name]);
  };

  const canNext = () => {
    if (step === 0) return !!objective;
    if (step === 1) return pickedSources.length > 0;
    if (step === 2) return !!businessType && !!location.trim();
    return true;
  };

  const next = () => { if (canNext() && step < TOTAL_STEPS - 1) setStep((s) => s + 1); };
  const back = () => { if (step > 0) setStep((s) => s - 1); };

  // Step 4 → run search through real engine
  const runEngine = async () => {
    setRunning(true);
    try {
      const { data } = await axios.get(`${API}/search-leads`, {
        params: {
          business_type: businessType,
          location,
          radius,
          score_max: scoreMax,
        },
      });
      setResults(data);
    } catch (_e) {
      toast.error(t("wiz.error_fetch"));
      setResults({ count: 0, leads: [], source: "error" });
    } finally { setRunning(false); }
  };

  // Step 4 finish → output
  const finishOutput = async () => {
    if (!results) return;
    if (output === "crm") {
      // Bulk save
      const leadsToSave = results.leads.slice(0, 50);
      let saved = 0;
      for (const ld of leadsToSave) {
        try { await axios.post(`${API}/save-lead`, ld); saved++; } catch (_e) { /* ignore */ }
      }
      toast.success(t("wiz.success_crm", { count: saved }));
      onClose();
      navigate("/kanban");
      return;
    }
    if (output === "campaign") {
      try {
        await axios.post(`${API}/campaigns`, {
          name: t("wiz.campaign_name", { type: businessType, loc: location }),
          subject: t("wiz.campaign_subject"),
          status: "DRAFT",
        });
        toast.success(t("wiz.success_campaign"));
        onClose();
        navigate("/campaigns");
      } catch (_e) { toast.error(t("wiz.error_fetch")); }
      return;
    }
    // CSV
    if (blockExportIfTrial(onTrial, "export CSV")) return;
    const rows = (results.leads || []).map((ld) => [
      ld.name, ld.phone || ld.phone_international || "", ld.website || "",
      ld.address || "", ld.rating || "", ld.review_count || "", ld.digital_score || "",
    ]);
    const header = ["name", "phone", "website", "address", "rating", "reviews", "score"];
    const csv = [header, ...rows].map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `leads-${Date.now()}.csv`;
    a.click(); URL.revokeObjectURL(url);
    toast.success(t("wiz.success_csv", { count: results.count }));
    onClose();
  };

  return (
    <div
      data-testid="prospecting-wizard"
      className="fixed inset-0 z-[55] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-[640px] al-card p-0 overflow-hidden shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between px-5 py-4 border-b border-[var(--al-border)]">
          <div className="flex items-center gap-2">
            <Zap className="w-4 h-4 text-[#3B82F6]" />
            <h2 className="text-[15px] font-bold text-[var(--al-text)]">{t("wiz.title")}</h2>
          </div>
          <button type="button" data-testid="wiz-close" onClick={onClose} className="text-[var(--al-text-muted)] hover:text-[var(--al-text)]">
            <X className="w-4 h-4" />
          </button>
        </header>

        {/* Progress dots */}
        <div className="flex items-center gap-1 px-5 py-2 border-b border-[var(--al-border)]" data-testid="wiz-progress">
          {Array.from({ length: TOTAL_STEPS }).map((_, i) => (
            <span
              key={`wiz-progress-${i}`}
              data-testid={`wiz-dot-${i}${i === step ? "-active" : ""}`}
              className="h-1 rounded-full flex-1 transition-colors"
              style={{ background: i <= step ? "#3B82F6" : "var(--al-border)" }}
            />
          ))}
        </div>

        <div className="p-5 min-h-[280px]">
          {step === 0 && <Step0 objective={objective} setObjective={setObjective} t={t} />}
          {step === 1 && <Step1 sources={sources} pickedSources={pickedSources} toggleSource={toggleSource} t={t} />}
          {step === 2 && (
            <Step2
              t={t}
              businessType={businessType} setBusinessType={setBusinessType}
              location={location} setLocation={setLocation}
              radius={radius} setRadius={setRadius}
              scoreMax={scoreMax} setScoreMax={setScoreMax}
            />
          )}
          {step === 3 && <Step3 t={t} running={running} runEngine={runEngine} results={results} />}
          {step === 4 && <Step4 t={t} output={output} setOutput={setOutput} results={results} />}
        </div>

        <footer className="flex items-center justify-between px-5 py-3 border-t border-[var(--al-border)] bg-[var(--al-bg)]">
          <button
            type="button"
            data-testid="wiz-back"
            onClick={back}
            disabled={step === 0}
            className="inline-flex items-center gap-1 h-9 px-3 rounded-lg border border-[var(--al-border)] text-[12.5px] disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <ChevronLeft className="w-3.5 h-3.5" /> {t("wiz.back")}
          </button>
          <span className="text-[11px] text-[var(--al-text-muted)]">{t("wiz.step_label", { current: step + 1, total: TOTAL_STEPS })}</span>
          {step < TOTAL_STEPS - 1 ? (
            <button
              type="button"
              data-testid="wiz-next"
              onClick={step === 3 ? next : next}
              disabled={!canNext() || (step === 3 && !results)}
              className="al-btn-primary inline-flex items-center gap-1 h-9 px-4 rounded-lg text-[12.5px] font-semibold disabled:opacity-50"
            >
              {t("wiz.next")} <ChevronRight className="w-3.5 h-3.5" />
            </button>
          ) : (
            <button
              type="button"
              data-testid="wiz-finish"
              onClick={finishOutput}
              disabled={!results || results.count === 0}
              className="al-btn-primary inline-flex items-center gap-1 h-9 px-4 rounded-lg text-[12.5px] font-semibold disabled:opacity-50"
            >
              <CheckCircle2 className="w-3.5 h-3.5" /> {t("wiz.finish")}
            </button>
          )}
        </footer>
      </div>
    </div>
  );
}

// ----- Wizard step components -----------------------------------------------------
function Step0({ objective, setObjective, t }) {
  const opts = [
    { id: "local_b2b",      icon: Building2, color: "#3B82F6", titleKey: "wiz.s0_local_title",    bodyKey: "wiz.s0_local_body" },
    { id: "validate_emails", icon: Mail,     color: "#A855F7", titleKey: "wiz.s0_validate_title", bodyKey: "wiz.s0_validate_body" },
    { id: "decision_makers", icon: Wand2,    color: "#F59E0B", titleKey: "wiz.s0_decision_title", bodyKey: "wiz.s0_decision_body" },
    { id: "high_rating_bulk", icon: Sparkles, color: "#22C55E", titleKey: "wiz.s0_bulk_title",    bodyKey: "wiz.s0_bulk_body" },
    { id: "custom",          icon: ListChecks, color: "#6B7280", titleKey: "wiz.s0_custom_title",  bodyKey: "wiz.s0_custom_body" },
  ];
  return (
    <div>
      <h3 className="text-[15px] font-bold text-[var(--al-text)]">{t("wiz.s0_title")}</h3>
      <p className="text-[12.5px] text-[var(--al-text-muted)] mt-1 mb-4">{t("wiz.s0_subtitle")}</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {opts.map((o) => {
          const Icon = o.icon;
          const active = objective === o.id;
          return (
            <button
              key={o.id}
              type="button"
              data-testid={`wiz-obj-${o.id}`}
              onClick={() => setObjective(o.id)}
              className={`text-left p-3 rounded-lg border-2 transition-all flex items-start gap-2.5 ${active ? "border-[#3B82F6] bg-[#EFF6FF]/60" : "border-[var(--al-border)] hover:border-[#3B82F6]/50"}`}
            >
              <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${o.color}1A`, color: o.color }}>
                <Icon className="w-4 h-4" />
              </div>
              <div className="min-w-0">
                <div className="text-[12.5px] font-bold text-[var(--al-text)]">{t(o.titleKey)}</div>
                <div className="text-[11px] text-[var(--al-text-muted)] mt-0.5 leading-snug">{t(o.bodyKey)}</div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function Step1({ sources, pickedSources, toggleSource, t }) {
  return (
    <div>
      <h3 className="text-[15px] font-bold text-[var(--al-text)]">{t("wiz.s1_title")}</h3>
      <p className="text-[12.5px] text-[var(--al-text-muted)] mt-1 mb-3">{t("wiz.s1_subtitle")}</p>
      <div className="max-h-[220px] overflow-y-auto pr-1 space-y-1.5">
        {sources.map((s) => {
          const checked = pickedSources.includes(s.name);
          return (
            <label
              key={s.id}
              data-testid={`wiz-src-${s.id}`}
              className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer ${checked ? "border-[#3B82F6] bg-[#EFF6FF]/40" : "border-[var(--al-border)] hover:border-[#3B82F6]/50"}`}
            >
              <input
                type="checkbox"
                checked={checked}
                onChange={() => toggleSource(s.name)}
                className="rounded text-[#3B82F6] focus:ring-[#3B82F6]"
              />
              <span className="flex-1 text-[12.5px] text-[var(--al-text)] truncate">{s.name}</span>
              <span className="text-[10px] font-semibold uppercase text-[var(--al-text-muted)] shrink-0">{s.type}</span>
            </label>
          );
        })}
      </div>
      <p className="text-[11px] text-[var(--al-text-muted)] mt-3">
        <strong>{pickedSources.length}</strong> {t("wiz.s1_selected")}
      </p>
    </div>
  );
}

function Step2({ t, businessType, setBusinessType, location, setLocation, radius, setRadius, scoreMax, setScoreMax }) {
  return (
    <div className="space-y-3">
      <h3 className="text-[15px] font-bold text-[var(--al-text)]">{t("wiz.s2_title")}</h3>
      <p className="text-[12.5px] text-[var(--al-text-muted)] -mt-1 mb-2">{t("wiz.s2_subtitle")}</p>

      <div>
        <label className="block text-[10.5px] uppercase tracking-wider font-bold text-[var(--al-text-muted)] mb-1">{t("wiz.s2_business")}</label>
        <input
          type="text"
          data-testid="wiz-business-type"
          value={businessType}
          onChange={(e) => setBusinessType(e.target.value)}
          placeholder={t("wiz.s2_business_placeholder")}
          className="al-input h-9 w-full text-[13px]"
        />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-[10.5px] uppercase tracking-wider font-bold text-[var(--al-text-muted)] mb-1">{t("wiz.s2_location")}</label>
          <input
            type="text"
            data-testid="wiz-location"
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            placeholder="Orlando, FL"
            className="al-input h-9 w-full text-[13px]"
          />
        </div>
        <div>
          <label className="block text-[10.5px] uppercase tracking-wider font-bold text-[var(--al-text-muted)] mb-1">{t("wiz.s2_radius")}</label>
          <select
            data-testid="wiz-radius"
            value={radius}
            onChange={(e) => setRadius(e.target.value)}
            className="al-input h-9 w-full text-[13px]"
          >
            {["5km", "10km", "20km", "30km", "50km"].map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>
      </div>
      <div>
        <label className="block text-[10.5px] uppercase tracking-wider font-bold text-[var(--al-text-muted)] mb-1">{t("wiz.s2_score")}</label>
        <select
          data-testid="wiz-score-max"
          value={scoreMax}
          onChange={(e) => setScoreMax(Number(e.target.value))}
          className="al-input h-9 w-full text-[13px]"
        >
          <option value={100}>{t("wiz.s2_score_any")}</option>
          <option value={66}>{t("wiz.s2_score_quente_morno")}</option>
          <option value={33}>{t("wiz.s2_score_quente")}</option>
        </select>
      </div>
    </div>
  );
}

function Step3({ t, running, runEngine, results }) {
  return (
    <div>
      <h3 className="text-[15px] font-bold text-[var(--al-text)]">{t("wiz.s3_title")}</h3>
      <p className="text-[12.5px] text-[var(--al-text-muted)] mt-1 mb-4">{t("wiz.s3_subtitle")}</p>
      {!results && !running && (
        <button
          type="button"
          data-testid="wiz-run"
          onClick={runEngine}
          className="al-btn-primary inline-flex items-center gap-1.5 h-10 px-4 rounded-lg text-[13px] font-semibold"
        >
          <Zap className="w-4 h-4" /> {t("wiz.s3_run")}
        </button>
      )}
      {running && (
        <div className="flex items-center gap-2 text-[13px] text-[var(--al-text-muted)]">
          <Loader className="w-4 h-4 animate-spin text-[#3B82F6]" /> {t("wiz.s3_running")}
        </div>
      )}
      {results && (
        <div data-testid="wiz-results" className="space-y-2">
          <div className="al-card p-3 bg-[#DCFCE7] border-[#BBF7D0]">
            <div className="text-[12.5px] font-bold text-[#16A34A]">
              <CheckCircle2 className="inline w-3.5 h-3.5 mr-1" />
              {t("wiz.s3_done", { count: results.count })}
            </div>
            <div className="text-[11px] text-[#16A34A] mt-0.5">
              {t("wiz.s3_source")}: {results.source}
            </div>
          </div>
          <ul className="max-h-[180px] overflow-y-auto text-[12px] divide-y divide-[var(--al-border)]">
            {(results.leads || []).slice(0, 10).map((ld, i) => (
              <li key={ld.id || `lead-${ld.name || i}`} className="py-1.5 flex items-center justify-between gap-2">
                <span className="truncate text-[var(--al-text)]">{ld.name}</span>
                <span className="text-[10.5px] text-[var(--al-text-muted)] tabular-nums shrink-0">{ld.digital_score ?? "—"}/100</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function Step4({ t, output, setOutput, results }) {
  const opts = [
    { id: "crm",      icon: Trello,          color: "#3B82F6", titleKey: "wiz.s4_crm_title",      bodyKey: "wiz.s4_crm_body" },
    { id: "campaign", icon: MailIcon,        color: "#A855F7", titleKey: "wiz.s4_campaign_title", bodyKey: "wiz.s4_campaign_body" },
    { id: "csv",      icon: FileSpreadsheet, color: "#22C55E", titleKey: "wiz.s4_csv_title",      bodyKey: "wiz.s4_csv_body" },
  ];
  return (
    <div>
      <h3 className="text-[15px] font-bold text-[var(--al-text)]">{t("wiz.s4_title")}</h3>
      <p className="text-[12.5px] text-[var(--al-text-muted)] mt-1 mb-3">
        {t("wiz.s4_subtitle", { count: results?.count || 0 })}
      </p>
      <div className="space-y-2">
        {opts.map((o) => {
          const Icon = o.icon;
          const active = output === o.id;
          return (
            <button
              key={o.id}
              type="button"
              data-testid={`wiz-out-${o.id}`}
              onClick={() => setOutput(o.id)}
              className={`w-full text-left p-3 rounded-lg border-2 transition-all flex items-start gap-3 ${active ? "border-[#3B82F6] bg-[#EFF6FF]/60" : "border-[var(--al-border)] hover:border-[#3B82F6]/50"}`}
            >
              <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${o.color}1A`, color: o.color }}>
                <Icon className="w-4 h-4" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[13px] font-bold text-[var(--al-text)]">{t(o.titleKey)}</div>
                <div className="text-[11.5px] text-[var(--al-text-muted)] mt-0.5">{t(o.bodyKey)}</div>
              </div>
              {active && <Check className="w-4 h-4 text-[#3B82F6] shrink-0" />}
            </button>
          );
        })}
      </div>
    </div>
  );
}
